/**
 * Ladies Hub - Premium Curated Marketplace
 * Full integration: ChipBar, CuratedRows, SubcategorySheet, FeaturedSellers, Animations
 * V2: Luxury hero redesign with dynamic greeting
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { formatMoney } from '../utils/formatters';
import { getAdTitle, getAdLocation } from '../utils/adI18n';
import { Heart, MapPin, Package, CheckCircle } from 'lucide-react';
import { toAbsoluteUrl } from '../lib/media';
import { trackAdEvent } from '../components/LadiesHubComponents.js';
import { guardImage } from '../lib/imageTrust';
import { usePostingChooser } from '../contexts/PostingChooserContext';
import LadiesLuxuryHero from '../components/LadiesLuxuryHero';
import LadiesHubChipBar from '../components/LadiesHubChipBar';
import LadiesHubSubcategorySheet from '../components/LadiesHubSubcategorySheet';
import LadiesHubCuratedRow from '../components/LadiesHubCuratedRow';
import FeaturedSellersCarousel from '../components/FeaturedSellersCarousel';
import { useHubAuthGate } from '../components/HubAuthGate';
import ViewToggle from '../components/ViewToggle';
import '../components/ViewToggle.css';
import useViewPreference from '../hooks/useViewPreference';
import { 
  getChipBarTabs, 
  getCuratedRows, 
  getCuratedRowLabel, 
  LadiesHubMotion,
  LadiesHubColors,
  LadiesHubShadows
} from '../hubs/ladies/LadiesHubPremiumConfig';
import { LadiesHubTheme } from '../theme/LadiesHubTheme';
import LadiesSellerOnboardingModal from '../components/LadiesSellerOnboardingModal';
import WomenOnlyOnboardingModal, { checkLadiesHubVerified, setLadiesHubVerified } from '../components/WomenOnlyOnboardingModal';
import InlineError from '../components/InlineError';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import './LadiesHub.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const motionConfig = LadiesHubMotion;
const { colors, radii, spacing } = LadiesHubTheme;

// Skeleton Loader for Product Grid
function ProductGridSkeleton({ count = 6 }) {
  return (
    <div className="ladies-products-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="ladies-product-card skeleton-card">
          <div className="product-image skeleton-shimmer" />
          <div className="product-info">
            <div className="skeleton-line" style={{ width: '80%', height: 12, marginBottom: 8 }} />
            <div className="skeleton-line" style={{ width: '50%', height: 14 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Verified Seller Badge (inline)
function VerifiedSellerBadge({ lang }) {
  return (
    <span
      className="verified-seller-badge"
      data-testid="verified-seller-badge"
    >
      <CheckCircle size={10} />
      {lang === 'ar' ? 'موثق' : 'Verified'}
    </span>
  );
}

export default function LadiesHub() {
  const { t, i18n } = useTranslation();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  
  const txt = useCallback((ar, en) => (isArabic ? ar : en), [isArabic]);

  // Hub Auth Gate - shows login modal for unauthenticated users
  const hubLang = isArabic ? 'ar' : 'en';
  const { AuthModal, WelcomeModal, setShowAuthModal } = useHubAuthGate('ladies-hub', hubLang);

  // View preference (list/grid toggle) - Ladies Hub defaults to grid (image-heavy)
  const { view: listingView, setView: setListingView } = useViewPreference('ladies');

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('cat') || 'all');
  const [activeSubcategory, setActiveSubcategory] = useState(searchParams.get('sub') || null);
  const [trackingFilter, setTrackingFilter] = useState('most_viewed'); // Tracking bar state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [showSubcategorySheet, setShowSubcategorySheet] = useState(false);
  const [selectedParentTab, setSelectedParentTab] = useState(null);
  
  // Curated rows state
  const [curatedData, setCuratedData] = useState({});
  const [curatedLoading, setCuratedLoading] = useState(true);
  
  // Verified sellers state (for Featured Sellers carousel)
  const [verifiedSellers, setVerifiedSellers] = useState([]);
  
  // Seller onboarding modal
  const [showSellerOnboarding, setShowSellerOnboarding] = useState(false);

  // Posting chooser context
  const { openChooser } = usePostingChooser();
  
  // Women-only verification modal
  const [showWomenOnboarding, setShowWomenOnboarding] = useState(false);
  const [isVerifiedWoman, setIsVerifiedWoman] = useState(true); // default true to avoid flash

  // Dynamic ticker items from backend
  const [tickerEn, setTickerEn] = useState(null);
  const [tickerAr, setTickerAr] = useState(null);

  // Real category counts from backend
  const [categoryCounts, setCategoryCounts] = useState({});

  // Build tabs from new config
  const tabs = useMemo(() => getChipBarTabs(), []);

  // Check women-only verification status on mount
  // Show onboarding only on first-ever visit OR when policy version changes
  useEffect(() => {
    const verified = checkLadiesHubVerified();
    setIsVerifiedWoman(verified);
    
    if (!verified) {
      // First-time visitor or policy version changed: show compact welcome
      setShowWomenOnboarding(true);
      // Also mark the generic HubWelcomeModal as seen to prevent double-modal overlap
      try {
        const welcomed = JSON.parse(localStorage.getItem('wassla_hub_welcomed_v1') || '{}');
        welcomed['ladies-hub'] = true;
        localStorage.setItem('wassla_hub_welcomed_v1', JSON.stringify(welcomed));
      } catch {}
    }
  }, []);

  // Fetch dynamic ticker items
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/ladies-hub/ticker`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.items_en?.length) setTickerEn(data.items_en);
        if (data?.items_ar?.length) setTickerAr(data.items_ar);
      })
      .catch(() => {});
  }, []);

  // Fetch real category counts
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/ladies-hub/category-counts`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.counts) setCategoryCounts(data.counts);
      })
      .catch(() => {});
  }, []);

  // Get current tab object
  const currentTab = useMemo(() => 
    tabs.find(t => t.id === activeTab) || tabs[0]
  , [tabs, activeTab]);
  
  // Curated rows config
  const curatedRows = useMemo(() => getCuratedRows(), []);

  // Fetch curated rows (frontend fallback - derive from existing listings)
  useEffect(() => {
    async function fetchCurated() {
      setCuratedLoading(true);
      try {
        const params = new URLSearchParams({ hub: 'ladies', limit: '30' });
        const res = await fetch(`${BACKEND_URL}/api/ads?${params}`);
        const data = await res.json();
        const allAds = data.ads || [];

        // Derive curated rows from existing data
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const derived = {
          editors_picks: allAds.slice(0, 10), // First 10 as editor's picks
          trending: [...allAds].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 10),
          new_today: allAds.filter(ad => {
            const created = new Date(ad.created_at);
            return created >= todayStart;
          }).slice(0, 10),
          top_sellers: allAds.filter(ad => ad.seller?.rating >= 4).slice(0, 10),
        };

        setCuratedData(derived);
        
        // Extract unique verified sellers from ads
        const sellersMap = new Map();
        allAds.forEach(ad => {
          if (ad.seller && (ad.seller.isVerifiedSeller === true || ad.seller.verification?.status === 'verified_boutique')) {
            const sellerId = ad.seller.user_id || ad.seller._id || ad.seller.email;
            if (sellerId && !sellersMap.has(sellerId)) {
              sellersMap.set(sellerId, ad.seller);
            }
          }
        });
        setVerifiedSellers(Array.from(sellersMap.values()));
      } catch (err) {
        console.error('Failed to derive curated data:', err);
        setCuratedData({});
        setVerifiedSellers([]);
      } finally {
        setCuratedLoading(false);
      }
    }
    fetchCurated();
  }, []);

  // Fetch products based on filters
  // When searchQuery exists, use strict=true for relevance ranking
  // Show empty state if strict search returns < 3 results
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const params = new URLSearchParams();
      params.set('hub', 'ladies');
      params.set('limit', '30');
      
      if (activeTab && activeTab !== 'all') {
        params.set('category', activeSubcategory || activeTab);
      }
      
      const trimmedQuery = searchQuery.trim();
      if (trimmedQuery) {
        params.set('search', trimmedQuery);
        // Enable strict relevance ranking ONLY when searching
        params.set('strict', 'true');
      }

      const response = await fetch(`${BACKEND_URL}/api/ads?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data.ads || []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      if (products.length === 0) setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeSubcategory, searchQuery]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — single source of truth
  // normalize → filter → dedupe (shared with Home, Shop, etc.)
  // ═══════════════════════════════════════════════════════════
  const displayProducts = useUnifiedSearch(products, searchQuery);

  // Navigation
  const handleBack = () => {
    if (hasInAppHistory()) navigate(-1);
    else navigate('/', { replace: true });
  };

  // Tab selection - IMPROVED UX: Open subcategory sheet on any main category click
  const handleTabSelect = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    
    // Set the active tab first
    setActiveTab(tabId);
    setActiveSubcategory(null);
    
    // Update URL params
    if (tabId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: tabId });
    }
    
    // If tab has subcategories (and is not 'all'), open the subcategory sheet
    if (tab && tab.subcategories?.length > 0 && tabId !== 'all') {
      setSelectedParentTab(tab);
      setShowSubcategorySheet(true);
    }
  };

  // Subcategory selection - handles both "View all" and specific subcategory
  const handleSubcategorySelect = (subcat) => {
    const parentId = selectedParentTab?.id || activeTab;
    
    if (subcat.isParent) {
      // "View all {Category}" - show all ads for main category
      setActiveTab(parentId);
      setActiveSubcategory(null);
      setSearchParams({ cat: parentId });
    } else {
      // Specific subcategory selected
      setActiveTab(parentId);
      setActiveSubcategory(subcat.id);
      setSearchParams({ cat: parentId, sub: subcat.id });
    }
  };
  
  // Navigate to seller's store/profile
  const handleSellerShopClick = (seller) => {
    const sellerId = seller.user_id || seller._id;
    if (sellerId) {
      // Navigate to seller's store listings
      navigate(`/shop?seller=${sellerId}`);
    }
  };

  // Search handlers
  // NOTE: We only set searchQuery here - the useEffect on fetchProducts will trigger the API call
  // because fetchProducts has searchQuery in its dependency array
  const commitSearch = useCallback((raw) => {
    const q = (raw || '').trim().replace(/\s+/g, ' ');
    if (!q) return;
    setSearchQuery(q);
  }, []);

  // Ticker chip click handler - filter by category and scroll to content
  const handleTickerChipClick = useCallback((categoryKey, chipData) => {
    // Set the active category tab
    setActiveTab(categoryKey);
    setActiveSubcategory(null);
    
    // Update URL params
    if (categoryKey === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: categoryKey });
    }

    // fetchProducts will be called automatically via useEffect when activeTab changes

    // Scroll to content section after a brief delay for state update
    setTimeout(() => {
      // Try to find the best scroll target in order of preference:
      // 1. Editor's Picks row
      // 2. Ladies feed section  
      // 3. ChipBar (fallback)
      const editorsPicksRow = document.querySelector('[data-testid^="curated-row-editor"]');
      const ladiesFeed = document.querySelector('.ladies-feed');
      const chipBar = document.querySelector('[data-testid="ladies-premium-chipbar"]');
      
      const targetElement = editorsPicksRow || ladiesFeed || chipBar;
      
      if (targetElement) {
        // Get offset to account for sticky header
        const offset = 80;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 150);
  }, [setSearchParams]);

  // Image URL helper — iter275 delegates to the canonical resolver so
  // relative `/api/uploads/…` paths fall back to same-origin on the
  // production custom domain (fixes ladies-hub cards showing the grey
  // placeholder on wassla.tech because BACKEND_URL was baked to the
  // stale preview host at build time).
  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('/') || image.startsWith('http://') || image.startsWith('https://')) {
      return toAbsoluteUrl(image);
    }
    // Bare filename → expand to /api/uploads/ then run through resolver
    return toAbsoluteUrl(`/api/uploads/${image}`);
  };

  return (
    <div className="ladies-hub-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="ladies-hub">
      {/* Hub Auth Gate - Login/Signup Modal for unauthenticated users */}
      {/* Suppressed when WomenOnlyOnboarding is active — it IS the entry gate for Ladies Hub */}
      {!showWomenOnboarding && <AuthModal />}
      {!showWomenOnboarding && <WelcomeModal />}
      
      {/* Luxury Hero Section with Dynamic Greeting */}
      <LadiesLuxuryHero
        lang={isArabic ? 'ar' : 'en'}
        onBack={handleBack}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearch={commitSearch}
        onTickerChipClick={handleTickerChipClick}
        {...(tickerEn ? { tickerItemsEn: tickerEn } : {})}
        {...(tickerAr ? { tickerItemsAr: tickerAr } : {})}
      />

      {/* Premium Sticky Chip Bar */}
      <LadiesHubChipBar
        tabs={tabs}
        activeTab={activeTab}
        onTabSelect={handleTabSelect}
        isRTL={isRTL}
        lang={isArabic ? 'ar' : 'en'}
        categoryCounts={categoryCounts}
      />

      {/* Light contextual posting hint */}
      <div className="ladies-posting-hint" data-testid="ladies-posting-hint">
        <span className="ladies-posting-hint-text">
          {txt('تريدين البيع هنا؟', 'Want to sell here?')}
        </span>
        <button
          className="ladies-posting-hint-link"
          onClick={() => openChooser('ladies')}
          data-testid="ladies-posting-hint-link"
        >
          {txt('أضيفي إعلان', 'Post a listing')}
        </button>
      </div>

      {/* Featured Verified Sellers Carousel (shows only if 3+ verified sellers) */}
      <FeaturedSellersCarousel
        sellers={verifiedSellers}
        onShopClick={handleSellerShopClick}
        lang={isArabic ? 'ar' : 'en'}
        isRTL={isRTL}
      />

      {/* Curated Content Rows (only show when viewing "All") */}
      <AnimatePresence>
        {activeTab === 'all' && !searchQuery && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={motionConfig.fadeSlide}
            className="ladies-curated-section"
          >
            {curatedRows.map((row) => (
              <LadiesHubCuratedRow
                key={row.id}
                title={getCuratedRowLabel(row, isArabic ? 'ar' : 'en')}
                products={curatedData[row.id] || []}
                loading={curatedLoading}
                onNavigate={(path) => {
                  const adId = path.split('/').pop();
                  trackAdEvent(adId, 'view');
                  navigate(path);
                }}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                lang={isArabic ? 'ar' : 'en'}
                isRTL={isRTL}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Products Feed */}
      <div className="ladies-feed">
        {/* Results Count with View Toggle */}
        <motion.div
          className="ladies-results-header"
          key={`results-${activeTab}-${activeSubcategory}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <span className="results-count">
            {loading
              ? txt('جارٍ التحميل...', 'Loading...')
              : txt(`${displayProducts.length} منتج`, `${displayProducts.length} products`)}
          </span>
          
          {/* View Toggle */}
          <ViewToggle
            view={listingView}
            onViewChange={setListingView}
            size="sm"
            disabled={loading}
          />
          
          {activeSubcategory && (
            <button
              className="btn-clear-filter"
              onClick={() => {
                setActiveSubcategory(null);
                setSearchParams({ cat: activeTab });
              }}
              data-testid="clear-subcategory-filter"
            >
              {txt('مسح الفلتر', 'Clear filter')} ✕
            </button>
          )}
        </motion.div>

        {/* Products Grid with AnimatePresence for smooth transitions */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ProductGridSkeleton count={6} />
            </motion.div>
          ) : fetchError && displayProducts.length === 0 ? (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <InlineError onRetry={fetchProducts} />
            </motion.div>
          ) : displayProducts.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="ladies-empty-state"
              data-testid="ladies-empty-state"
            >
              {/* Search-specific empty state */}
              {searchQuery.trim() ? (
                <>
                  <div className="empty-icon">🔍</div>
                  <h3 data-testid="search-no-results-title">
                    {txt(`لا توجد نتائج لـ "${searchQuery}"`, `No results for "${searchQuery}"`)}
                  </h3>
                  <p className="empty-suggestions-label">
                    {txt('جرّبي:', 'Try:')}
                  </p>
                  <div className="empty-suggestions">
                    <button 
                      className="empty-suggestion-chip"
                      onClick={() => setSearchQuery('مجوهرات')}
                    >
                      {txt('مجوهرات', 'Jewellery')}
                    </button>
                    <button 
                      className="empty-suggestion-chip"
                      onClick={() => setSearchQuery('إكسسوارات')}
                    >
                      {txt('إكسسوارات', 'Accessories')}
                    </button>
                    <button 
                      className="empty-suggestion-chip"
                      onClick={() => setSearchQuery('فساتين')}
                    >
                      {txt('فساتين', 'Dresses')}
                    </button>
                  </div>
                  <button
                    className="btn-clear-search"
                    onClick={() => setSearchQuery('')}
                    data-testid="clear-search-btn"
                  >
                    {txt('مسح البحث', 'Clear search')}
                  </button>
                </>
              ) : (
                <>
                  <div className="empty-icon">🌸</div>
                  <h3>{txt('لا توجد إعلانات هنا بعد', 'No listings yet')}</h3>
                  <p>{txt('كوني أول من يضيف!', 'Be the first to post!')}</p>
                  <button
                    className="btn-post-ladies"
                    onClick={() => openChooser('ladies')}
                    data-testid="ladies-post-cta"
                  >
                    {txt('أضيفي إعلان', 'Post a listing')}
                  </button>
                  {activeTab !== 'all' && (
                    <button
                      className="btn-reset-orange"
                      onClick={() => handleTabSelect('all')}
                    >
                      {txt('عرض جميع المنتجات', 'Show all products')}
                    </button>
                  )}
                </>
              )}
            </motion.div>
          ) : listingView === 'list' ? (
            /* List View */
            <motion.div
              key="products-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="ladies-products-list"
            >
              {displayProducts.map((product, index) => {
                const categoryKey = product.ladies_category || product.category_id || product.category;
                const imageUrl = guardImage(product.images?.[0], categoryKey);
                const title = getAdTitle(product, lang).text || product.title;
                const location = getAdLocation(product, lang);
                const isVerified = product.seller?.verification?.status === 'verified_boutique';
                const priceDisplay = formatMoney(product.price, {
                  locale: isArabic ? 'ar-SD' : 'en',
                  currency: product.currency || 'SDG',
                });

                return (
                  <motion.div
                    key={product.ad_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="ladies-list-card"
                    onClick={() => {
                      trackAdEvent(product.ad_id, 'view');
                      navigate(`/ad/${product.ad_id}`);
                    }}
                    data-testid={`ladies-list-${product.ad_id}`}
                  >
                    <div className="llc-image">
                      {imageUrl ? (
                        <img src={imageUrl} alt={title} loading="lazy" />
                      ) : (
                        <div className="llc-placeholder">
                          <Package size={24} />
                        </div>
                      )}
                      {isVerified && (
                        <div className="llc-verified">
                          <CheckCircle size={8} />
                        </div>
                      )}
                    </div>
                    <div className="llc-content">
                      <p className="llc-price">{priceDisplay}</p>
                      <h3 className="llc-title">{title}</h3>
                      {location && (
                        <div className="llc-location">
                          <MapPin size={11} />
                          <span>{location}</span>
                        </div>
                      )}
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.85 }}
                      onClick={(e) => toggleFavorite(product.ad_id, e)}
                      className={`llc-favorite ${isFavorite(product.ad_id) ? 'active' : ''}`}
                      data-testid={`favorite-list-${product.ad_id}`}
                    >
                      <Heart size={14} fill={isFavorite(product.ad_id) ? 'currentColor' : 'none'} />
                    </motion.button>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            /* Grid View */
            <motion.div
              key="products-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="ladies-products-grid"
            >
              {displayProducts.map((product, index) => {
                const categoryKey = product.ladies_category || product.category_id || product.category;
                const imageUrl = guardImage(product.images?.[0], categoryKey);
                const isSvgPlaceholder = imageUrl && imageUrl.startsWith('/assets/placeholders/');
                const title = getAdTitle(product, lang).text || product.title;
                const location = getAdLocation(product, lang);
                const isVerified = product.seller?.verification?.status === 'verified_boutique';

                return (
                  <motion.div
                    key={product.ad_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03, ...motionConfig.fadeSlide }}
                    whileTap={{ scale: 0.98 }}
                    className="ladies-product-card"
                    onClick={() => {
                      trackAdEvent(product.ad_id, 'view');
                      navigate(`/ad/${product.ad_id}`);
                    }}
                    data-testid={`ladies-product-${product.ad_id}`}
                  >
                    <div className="product-image">
                      {imageUrl && !isSvgPlaceholder ? (
                        <img
                          src={imageUrl}
                          alt={title}
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {isSvgPlaceholder ? (
                        <div className="image-placeholder category-placeholder" style={{ display: 'flex' }}>
                          <img src={imageUrl} alt={categoryKey} style={{ width: 64, height: 64, opacity: 0.7 }} />
                        </div>
                      ) : (
                        <div
                          className="image-placeholder"
                          style={{ display: imageUrl && !isSvgPlaceholder ? 'none' : 'flex' }}
                        >
                          <Package size={32} />
                          <span className="placeholder-text">
                            {txt('لا توجد صورة بعد', 'No image yet')}
                          </span>
                        </div>
                      )}

                      {/* Verified Badge Overlay */}
                      {isVerified && (
                        <div className="verified-overlay" data-testid={`verified-overlay-${product.ad_id}`}>
                          <CheckCircle size={10} />
                          {txt('موثق', 'Verified')}
                        </div>
                      )}

                      <motion.button
                        whileTap={{ scale: 0.85 }}
                        onClick={(e) => toggleFavorite(product.ad_id, e)}
                        className={`favorite-btn ${isFavorite(product.ad_id) ? 'active' : ''}`}
                        data-testid={`favorite-${product.ad_id}`}
                      >
                        <Heart
                          size={18}
                          fill={isFavorite(product.ad_id) ? 'currentColor' : 'none'}
                        />
                      </motion.button>
                    </div>
                    <div className="product-info">
                      <h3 className="product-title">{title}</h3>
                      {location && (
                        <div className="product-location">
                          <MapPin size={12} />
                          <span>{location}</span>
                        </div>
                      )}
                      <p className="product-price">
                        {formatMoney(product.price, {
                          locale: isArabic ? 'ar-SD' : 'en',
                          currency: product.currency || 'SDG',
                        })}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Safety Notice */}
      <div className="ladies-safety-notice">
        <div className="safety-icon">🔒</div>
        <p className="safety-text">
          {txt(
            'جميع الإعلانات تخضع لمراجعة للحفاظ على بيئة آمنة وموثوقة',
            'All listings are reviewed to maintain a safe and trusted environment'
          )}
        </p>
      </div>

      {/* Subcategory Bottom Sheet */}
      <LadiesHubSubcategorySheet
        isOpen={showSubcategorySheet}
        onClose={() => setShowSubcategorySheet(false)}
        parentTab={selectedParentTab}
        subcategories={selectedParentTab?.subcategories || []}
        onSelect={handleSubcategorySelect}
        lang={isArabic ? 'ar' : 'en'}
      />
      
      {/* Ladies Seller Onboarding Modal */}
      <LadiesSellerOnboardingModal
        isOpen={showSellerOnboarding}
        onClose={() => setShowSellerOnboarding(false)}
        onContinue={() => {
          setShowSellerOnboarding(false);
          navigate('/add-ad', { 
            state: { 
              hub: 'ladies',
              posting_context: 'ladies',
              default_main_category: activeTab !== 'all' ? activeTab : null
            } 
          });
        }}
        lang={isArabic ? 'ar' : 'en'}
      />
      
      {/* Women-Only Onboarding Modal - shown on first visit or policy update */}
      <WomenOnlyOnboardingModal
        isOpen={showWomenOnboarding}
        onConfirm={() => {
          setShowWomenOnboarding(false);
          setIsVerifiedWoman(true);
          // Suppress the auth gate entirely — user already entered via onboarding
          setShowAuthModal(false);
          try {
            const dismissed = JSON.parse(localStorage.getItem('wassla_hub_auth_dismissed_v1') || '{}');
            dismissed['ladies-hub'] = true;
            localStorage.setItem('wassla_hub_auth_dismissed_v1', JSON.stringify(dismissed));
          } catch {}
        }}
        onExplore={() => {
          setShowWomenOnboarding(false);
          setIsVerifiedWoman(true);
        }}
        onExit={() => {
          setShowWomenOnboarding(false);
          setIsVerifiedWoman(true);
          navigate('/');
        }}
        lang={isArabic ? 'ar' : 'en'}
      />
    </div>
  );
}
